import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  offset,
  Placement,
  shift,
  useFloating,
} from "@floating-ui/react";
import { ReactElement, ReactNode, useEffect, useRef, useState } from "react";
import colors from "tailwindcss/colors";

const TOOLTIP_DELAY_MS = 500;

type TooltipProps = {
  children: ReactNode;
  content: string;
  position?: Placement;
};

export const Tooltip = ({
  children,
  content,
  position = "top",
}: TooltipProps): ReactElement => {
  const [isVisible, setIsVisible] = useState(false);
  const arrowRef = useRef(null);
  const showTimeoutRef = useRef<null | ReturnType<typeof setTimeout>>(null);

  useEffect((): (() => void) => {
    return () => {
      if (showTimeoutRef.current) clearTimeout(showTimeoutRef.current);
    };
  }, []);

  const { context, refs, strategy, x, y } = useFloating({
    middleware: [
      // Add some distance between the tooltip and the target
      offset(8),
      // Flip the tooltip if there's not enough space
      flip(),
      // Shift the tooltip if it would go offscreen
      shift({ padding: 8 }),
      // Set up the arrow
      arrow({ element: arrowRef }),
    ],
    onOpenChange: setIsVisible,
    open: isVisible,
    placement: position,
    // Keep position updated when scrolling, resizing, etc
    whileElementsMounted: autoUpdate,
  });

  const handleMouseEnter = (): void => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
    showTimeoutRef.current = setTimeout(() => {
      showTimeoutRef.current = null;
      setIsVisible(true);
    }, TOOLTIP_DELAY_MS);
  };

  const handleMouseLeave = (): void => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
    setIsVisible(false);
  };

  return (
    <div className="relative">
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        ref={refs.setReference}
      >
        {children}
      </div>

      {isVisible && (
        <div
          className="whitespace-pre-wrap rounded-md bg-white px-3 py-2 text-sm text-black shadow-md"
          ref={refs.setFloating}
          style={{
            left: x ?? 0,
            maxWidth: "350px",
            position: strategy,
            top: y ?? 0,
            width: "max-content",
            zIndex: 10,
          }}
        >
          {content}

          <FloatingArrow
            context={context}
            fill={colors.white}
            height={8}
            ref={arrowRef}
            width={8}
          />
        </div>
      )}
    </div>
  );
};
